import { defineComponent as _defineComponent } from 'vue'
import {computed, getCurrentInstance, onMounted, Ref, ref, watch} from 'vue';
import {DataTableHeader} from '@/services/searchEngine/interfaces/DataTableHeader';
import {NPSFilter} from '@/models/Filter';
import FpDataTable from '@/components/datatables/FpDataTable.vue';
import TablePagination from '@/components/commons/TablePagination.vue';
import PersonAvatar from '@/components/commons/PersonAvatar.vue';
import {NPSFeedback, NPSOpinion} from '@/modules/nps/models/NPS';
import GoToService from '@/router/GoToService';
import User from '@/models/user/User';
import UserApi from '@/modules/user/services/UserApi';
import NPSApi from '@/modules/nps/services/NPSApi';
import LRating from '@/components/stats/LRating.vue';
import {cloneDeep, isEqual} from 'lodash-es';
import FpFilter from '@/components/inputs/FpFilter.vue';
import BranchTypes from '@/modules/branches/services/BranchTypes';
import {FilterType} from '@/services/searchEngine/enums/FilterType';
import {FeedbackSourceType} from '@/modules/nps/enums/FeedbackSourceType';
import {NPSReplacementType} from '@/modules/nps/enums/NPSReplacementType';
import {useInstance} from '@/composables/useInstance';

type RatingConfig = {
  model: keyof NPSFeedback,
  label?: string,
  tooltip?: string,
  simple?: boolean,
}

type NPSTableElement = {
  id: number,
  user: number,
  branch: string,
  client: number,
  createdAt: string,
  ratings: Array<number>,
  opinion: string,
  management: any,
  deal: string,
  source: string,
  load: boolean,
  clientPhone: string,
  resendAction: never,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NPSDataTable',
  props: {
    opinions: null,
    params: null,
    short: { type: Boolean },
    simple: { type: Boolean },
    showResendAction: { type: Boolean, default: false },
    resendSurveyLoadingUuid: { default: null }
  },
  emits: ['resend-survey',],
  setup(__props: any, { emit }) {

const props = __props as { opinions?: Array<NPSOpinion>, params?: NPSFilter, short?: boolean, simple?: boolean, showResendAction: boolean, resendSurveyLoadingUuid: string | null };

const { $i18n, $snackbarService, $errorHandler } = useInstance();
const t = $i18n.useNPSDataTable;




const MAX_INITIALS_LENGTH = 3;

const items: Ref<Array<NPSOpinion>> = ref([]);
const users: Ref<Array<User>> = ref([]);
const options = ref<NPSFilter>(new NPSFilter());
const oldOptions: Ref<NPSFilter> = ref(cloneDeep(options.value));
const loading: Ref<boolean> = ref(false);
const instance = getCurrentInstance();

const headersConfig = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return [
    {text: t.ID, value: 'id', valueSort: 'id', condition: !props.short,},
    {
      text: t.EXPERT,
      value: 'user',
      valueSort: 'userIds',
      valueFilter: 'users',
      filterType: FilterType.AUTOCOMPLETE,
      condition: !props.short,
    },
    {text: t.BRANCH, value: 'branch', valueSort: 'branch', condition: !props.short, },
    {text: t.CLIENT, value: 'client', },
    {text: t.CREATED_AT, value: 'createdAt', valueFilter: 'createdAt', filterType: FilterType.DATE, valueSort: 'createdAt' && (!props.short && !props.simple) , },
    {text: t.RATINGS, value: 'ratings', valueFilter: 'rating', filterType: FilterType.RANGE, range: {from: 1, to: 5,},},
    {text: t.OPINION, value: 'opinion', },
    {text: t.MANAGEMENT, value: 'management', condition: !props.short, },
    {text: t.DEAL, value: 'deal', },
    {text: t.SOURCE, value: 'source', valueSort: 'source', condition: !props.short, },
    {text: t.LEAD, value: 'lead', condition: !props.short, },
    {text: t.CLIENT_PHONE, value: 'clientPhone', valueFilter: 'clientPhone', filterType: FilterType.TEXT,},
    {text: t.RESEND_ACTION_COLUMN, value: 'resendAction', condition: !!props.showResendAction, },
  ];
});

const ratingsConfig = computed<Array<RatingConfig>>(() => {
  return [
    {
      model: 'substantivePartOfMeeting',
      label: t.KNOWLEDGE,
    },
    {
      model: 'expertInvolvement',
      label: t.INVOLVEMENT,
    },
    {
      model: 'expertBehavior',
      label: t.PERSONAL_CULTURE,
    },
  ];
});

const visibleElements = computed<Partial<Record<keyof NPSTableElement, boolean>>>(() => {
  const visibilityConfig: Partial<Record<keyof NPSTableElement, boolean>> = {};
  headersConfig.value.map(item => {
    const key = item.value as keyof NPSTableElement;
    visibilityConfig[key] = item.condition === undefined || item.condition;
  });

  return visibilityConfig;
});

const headers = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return headersConfig.value
    .filter(item => item.condition === undefined || item.condition)
    .map(x => new DataTableHeader<NPSTableElement, NPSFilter>(x));
});

onMounted(() => {
  if (props.params) {
    options.value = props.params;
    cloneOptions();
  }

  if (props.opinions) {
    items.value = props.opinions;
    fetchUsers();
  } else {
    fetchData();
  }
});

watch(() => props.opinions, (opinions) => {
  if (!opinions) {
    return;
  }

  items.value = opinions;
  fetchUsers();
}, {deep: true,});

const onUpdateOptions = (newValue: NPSFilter) => {
  if (isEqual(cloneDeep(oldOptions.value), cloneDeep(options.value))) {
    return;
  }

  if (!props.simple && options.value.page) {
    GoToService.setQuery({page: String(options.value.page), }, false);
  }

  cloneOptions();
  fetchData();
};

const filterTextOpinion = async() => {
  if (!options.value.hasTextOpinion) delete options.value.hasTextOpinion;
  await fetchData();
};

const fetchData = async() => {
  loading.value = true;
  try {
    options.value.page = instance?.proxy.$route.query.page ? Number(instance?.proxy.$route.query.page) : 1;
    const response = await NPSApi.getOpinions(options.value);
    items.value = response.items;
    options.value.quantity = response.quantity;
    options.value.page = response.page || options.value.page;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }

  cloneOptions();
  fetchUsers();
};

const getUsersIdsToFetch = () => {
  const usersIds: Array<number> = [];
  items.value.map(item => {
    if (!usersData.value[item.survey.userId] && !usersIds.includes(item.survey.userId)) {
      usersIds.push(item.survey.userId);
    }
  });

  return usersIds;
};

const fetchUsers = async() => {
  if (props.short) {
    return;
  }

  const usersIds = getUsersIdsToFetch();
  if (!usersIds.length) {
    return;
  }

  const response = await UserApi.usersForFilters({users: usersIds,});
  if (response) {
    users.value.push(...response);
  }
};

const usersData = computed<Record<number, User>>(() => {
  const usersMap: Record<number, User> = {};
  users.value.map(user => {
    usersMap[user.id] = user;
  });

  return usersMap;
});

const clientsInitials = computed<Record<number, string>>(() => {
  const initials: Record<number, string> = {};

  items.value.map(item => {
    initials[item.survey.clientId] = (item.survey.clientName.split(' ').map(x => x.substring(0, 1).toUpperCase()).join('') || '').substring(0,MAX_INITIALS_LENGTH);
  });

  return initials;
});

const goToUserDetails = (userId: number) => {
  if (userId) {
    GoToService.user(userId);
  }
};

const goToClientDetails = (clientId: number) => {
  if (clientId) {
    GoToService.client(clientId);
  }
};

const goToDeal = (dealId: number) => {
  if (dealId) {
    GoToService.deal(dealId);
  }
};

const goToBranch = (branchId: number) => {
  if (branchId) {
    GoToService.branch(branchId);
  }
};

const onPublishChange = (item: NPSOpinion) => {
  item.feedback!.calculate = item.feedback!.publish;
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const onCalculateChange = (item: NPSOpinion) => {
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const processOpinion = async(item: NPSOpinion, publish: boolean = false, calculate: boolean = false) => {
  try {
    await NPSApi.pocessOpinion(item.feedback!.id, { publish, calculate, });
    $snackbarService.openSuccessSnackbar(t.OPINION_STATUS_CHANGED);
  } catch (e) {
    $errorHandler(e, t.OPINION_PROCESS_ERROR);
  }
};

const isResendBlocked = (item: NPSOpinion) => {
  return item.survey.replacement === NPSReplacementType.CanNotBeReplaced || !!item.survey.replacementId?.trim();
};

const onResendSurvey = (item: NPSOpinion) => {
  emit('resend-survey', item);
};

const cloneOptions = () => {
  oldOptions.value = cloneDeep(options.value);
};

return { __sfc: true,$i18n, $snackbarService, $errorHandler, t, props, emit, MAX_INITIALS_LENGTH, items, users, options, oldOptions, loading, instance, headersConfig, ratingsConfig, visibleElements, headers, onUpdateOptions, filterTextOpinion, fetchData, getUsersIdsToFetch, fetchUsers, usersData, clientsInitials, goToUserDetails, goToClientDetails, goToDeal, goToBranch, onPublishChange, onCalculateChange, processOpinion, isResendBlocked, onResendSurvey, cloneOptions, FpDataTable, TablePagination, PersonAvatar, LRating, FpFilter, BranchTypes, FeedbackSourceType }
}

})